﻿// ── Auth ──────────────────────────────────────────────────────────────────────
// Handles session management and user profile loading.
// Login / registration lives in login.html + js/login.js.
// Unauthenticated users are redirected to login.html.

let currentUser    = null;
let currentProfile = null;

// ── HTML escaping ─────────────────────────────────────────────────────────────
// auth.js is loaded standalone by several pages (no shared admin/app-scope
// helper guaranteed to exist yet), so it gets its own tiny one — same shape
// as app.js's _fieldEsc / admin's esc(). Every project/document field this
// file renders comes from the database (project names are admin-authored;
// document title/type are insertable by any foreman assigned to the project
// per the `documents: member insert` RLS policy) — never trust it into
// innerHTML unescaped.
function _authEsc(str) {
  return String(str ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Only render a URL as a real link when its scheme is one a browser will just
// navigate/display (http/https or scheme-relative/relative) — anything else
// (javascript:, data:, vbscript:, …) is dropped so a poisoned file_url can't
// run script when the link is clicked. Returned value is HTML-attribute-safe.
function _authSafeUrl(url) {
  // Browsers drop tabs/newlines inside a scheme ("java	script:"), so strip
  // them first, then accept a plain https link only.
  const u = String(url ?? '').replace(/[\u0000-\u0020\u007f]/g, '');
  if (!u) return '';
  try { if (new URL(u).protocol !== 'https:') return ''; } catch { return ''; }
  return _authEsc(u);
}

// ── Offline cache (profile + projects) ────────────────────────────────────────
// Persist the essentials so the app can boot on-site with no signal instead of
// bouncing the foreman to the login page. Keyed by user id for shared devices.
function _cacheProfile(uid, profile) {
  try { localStorage.setItem('ordinex_profile_' + uid, JSON.stringify(profile)); } catch {}
}
function _loadCachedProfile(uid) {
  try { const s = localStorage.getItem('ordinex_profile_' + uid); return s ? JSON.parse(s) : null; } catch { return null; }
}
function _cacheProjects(uid, payload) {
  try { localStorage.setItem('ordinex_projects_' + uid, JSON.stringify(payload)); } catch {}
}
function _loadCachedProjects(uid) {
  try {
    const v = JSON.parse(localStorage.getItem('ordinex_projects_' + uid) || 'null');
    if (Array.isArray(v)) {                             // old format: names only
      // Sub-projects: this format predates parent_id — synthesize parent-less
      // rows (parent_id null) so window.projectRows always matches the shape
      // loadUserProjects() produces. filterFilableProjects() hides nothing
      // when every parent_id is null: an old cache degrades to "no
      // buildings" instead of hiding everything for lack of data.
      window.projectRows = v.map(name => ({ id: name, name, parent_id: null }));
      return v;
    }
    if (v && Array.isArray(v.names)) {
      window.projectTrades = v.trades || {};
      // Same idea: a cache written before the buildings feature has no
      // `rows` (or one whose length drifted for any reason) — synthesize to
      // keep the same shape and the same degrade behavior as above.
      window.projectRows = (Array.isArray(v.rows) && v.rows.length === v.names.length)
        ? v.rows
        : v.names.map(name => ({ id: name, name, parent_id: null }));
      return v.names;
    }
  } catch {}
  return null;
}
function _isNetworkError(err) {
  const m = (err && (err.message || String(err))) || '';
  return /fetch|network|offline|failed to|load failed/i.test(m);
}
function _loadCachedTaskNames(uid) {
  try { return JSON.parse(localStorage.getItem('ordinexTaskNames:' + uid)) || {}; }
  catch { return {}; }
}

// Task (activity) names per project, for the timesheet's Activity picker.
// Names only — the RPC's return type has no money in it, by design.
async function loadProjectTasks(userId) {
  try {
    const { data, error } = await sbClient.rpc('project_task_names');
    if (error) throw error;
    const byProject = {};
    (data || []).forEach(r => {
      (byProject[r.project_name] = byProject[r.project_name] || []).push(
        { task_id: r.task_id, description: r.description, sort_order: r.sort_order,
          material_type_id: r.material_type_id || null });
    });
    try { localStorage.setItem('ordinexTaskNames:' + userId, JSON.stringify(byProject)); } catch (_) {}
    return byProject;
  } catch (_) {
    return _loadCachedTaskNames(userId);
  }
}

// The supabase client persists its session under sb-<ref>-auth-token. Reading
// it directly is the only way to know "someone logged in on this device
// before" when getSession() returns null because an EXPIRED token could not
// refresh with no signal. Returns the stored user, or null.
function _readStoredAuthUser() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!/^sb-.+-auth-token$/.test(k)) continue;
      const v = JSON.parse(localStorage.getItem(k));
      const sess = v?.currentSession || v;   // shape differs across auth-js versions
      if (sess?.user?.id && sess?.refresh_token) return sess.user;
    }
  } catch (_) { /* unreadable storage — treat as no stored login */ }
  return null;
}

// ── Bootstrap ─────────────────────────────────────────────────────────────────
async function initAuth() {
  // Check for an existing session (e.g. user refreshed the page).
  // Raced against a short timeout: with an EXPIRED token and no signal,
  // auth-js sits in a fetch-retry loop inside getSession() for tens of
  // seconds — the foreman would stare at a dead splash screen. On timeout we
  // treat it as "no session" and let the offline grace below decide; if the
  // retry loop later wins, the client simply becomes healthy underneath us.
  const raced = await Promise.race([
    sbClient.auth.getSession().catch(() => null),
    new Promise(resolve => setTimeout(() => resolve('TIMEOUT'), 2500)),
  ]);
  const session = raced === 'TIMEOUT' ? null : (raced?.data?.session || null);
  if (!session) {
    // Offline grace (PWA hardening, item 1): with no signal, an expired
    // access token used to bounce the foreman to a login page he cannot log
    // in on — locked out of an installed app with his forms cached. If a
    // previous login is still stored, boot from it: onAuthSuccess()'s
    // existing offline fallback restores the cached profile/projects, and
    // the refresh fires the moment connectivity returns (a refresh REJECTED
    // while online — revoked account — still signs out like before). A
    // device that never logged in has no stored token → login, as always.
    const storedUser = _readStoredAuthUser();
    if (storedUser && !navigator.onLine && _bootFromCache(storedUser)) {
      // Straight to the caches — the poisoned client (stuck in its refresh
      // retry loop) must not be awaited for anything on this path.
      window.addEventListener('online', () => {
        sbClient.auth.refreshSession().then(({ data, error }) => {
          if (data?.session) { if (typeof flushOutbox === 'function') flushOutbox(); }
          else if (error && !_isNetworkError(error)) window.location.href = 'login.html';
        }).catch(() => {});
      });
      return;
    }
    window.location.href = 'login.html';
    return;
  }

  if (sessionIsDeactivated(session)) {
    try { await sbClient.auth.signOut({ scope: 'local' }); } catch (_) {}
    window.location.href = 'login.html?deactivated=1';
    return;
  }

  await onAuthSuccess(session.user);

  // React to sign-out events (sign-in is handled by login.html redirecting here)
  sbClient.auth.onAuthStateChange(async (event, session) => {
    if (event === 'SIGNED_OUT') window.location.href = 'login.html';
    // a new token carries the plan claim: the read-only banner follows it both ways
    if (event === 'TOKEN_REFRESHED') setTimeout(() => _showPlanBannerIfNeeded(), 0);
  });
}

// Boot the app entirely from the offline caches (profile + projects + org's
// own localStorage cache) — ZERO awaits on the supabase client, which may be
// wedged in a token-refresh retry loop when the session expired offline.
// Returns false when this device has no usable cache (it never finished an
// online boot); the caller decides where to send the user.
function _bootFromCache(user) {
  const cached = _loadCachedProfile(user.id);
  if (!cached || !cached.full_name) return false;
  currentUser = user;
  window.currentUser = user;
  currentProfile = cached;
  window.currentProfile = cached;
  window.userProjects = _loadCachedProjects(user.id) || [];
  window.projectTasks = _loadCachedTaskNames(user.id);
  window.Ordinex?.org.load(sbClient).then(org => {
    if (org) {
      window.currentOrg = org;
      window.Ordinex.shell.apply(sbClient, org);
      if (typeof applyTradePacks === 'function') applyTradePacks();   // W5e
      // the time clock is on or off per company: its cards and doors show once the org is known
      if (typeof _clockRefreshSurfaces === 'function') _clockRefreshSurfaces();
    }
  }).catch(() => {});
  updateHeaderForUser(cached);
  showApp();
  initializeApp();
  _showPlanBannerIfNeeded();   // W6
  if (typeof flushOutbox === 'function') flushOutbox();
  return true;
}

// ── After successful login ────────────────────────────────────────────────────
async function onAuthSuccess(user) {
  currentUser = user;
  window.currentUser = user;

  // Load profile
  const { data: profile, error } = await sbClient
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error || !profile) {
    // Offline fallback: reuse the last good profile so the app still opens on-site.
    // Only when it looks like a connectivity problem — a real auth failure while
    // online should still sign the user out.
    if ((navigator.onLine === false || _isNetworkError(error)) && _bootFromCache(user)) return;
    console.error('Profile load error:', error);
    // Clear the session on this device BEFORE leaving: login.html forwards
    // any live session straight back here, so a session left behind made
    // the two pages bounce forever. scope 'local' makes no network call,
    // so it cannot hang offline. The server sign-out stays fire-and-forget.
    try { await sbClient.auth.signOut({ scope: 'local' }); } catch (_) {}
    window.location.href = 'login.html';
    return;
  }

  currentProfile = profile;
  window.currentProfile = profile;
  _cacheProfile(user.id, profile);

  // Tenant context: org row + branding (accent, logo, name). Non-blocking —
  // the app shell works with defaults until it resolves; offline boots reuse
  // the org.load() localStorage cache.
  window.Ordinex?.org.load(sbClient).then(org => {
    if (org) {
      window.currentOrg = org;
      window.Ordinex.shell.apply(sbClient, org);
      if (typeof applyTradePacks === 'function') applyTradePacks();   // W5e
      // the time clock is on or off per company: its cards and doors show once the org is known
      if (typeof _clockRefreshSurfaces === 'function') _clockRefreshSurfaces();
    }
  }).catch(() => {});
  // Form schemas → cache, so submissions can stamp schema_id/version (W5c).
  window.Ordinex?.forms.load(sbClient).catch(() => {});

  // First-time login: if name not set yet, ask for it before showing the app
  if (!profile.full_name || !profile.full_name.trim()) {
    showProfileSetup();
    return;
  }

  // Fetch projects assigned to this user
  window.userProjects = await loadUserProjects();
  window.projectTasks = await loadProjectTasks(user.id);

  // Update header
  updateHeaderForUser(profile);

  // Start the main app
  showApp();
  initializeApp();   // defined in app.js
  _showPlanBannerIfNeeded({ refresh: true });   // W6

  // Send anything that was saved while offline on a previous visit
  if (typeof flushOutbox === 'function') flushOutbox();
}

// ── W6: plan banner — read-only plans warn the crew (claim-driven, no query) ──
// The claims of the token stored on this device. Sync and offline-safe: never
// awaits the client (it can be wedged in a refresh loop with no signal).
function _storedToken() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!/^sb-.+-auth-token$/.test(k)) continue;
      const v = JSON.parse(localStorage.getItem(k));
      const tok = (v?.currentSession || v)?.access_token;
      if (tok) return JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    }
  } catch { /* unreadable token: treat as no claim */ }
  return {};
}
function _storedPlanClaims() { return _storedToken().app_metadata || {}; }
// The plan status when the company is read-only, else null. The server's rule
// exactly (current_plan_ok(): no claim reads as 'active').
function orgPlanReadOnly() {
  const s = _storedPlanClaims().plan_status;
  return s && !['active', 'trialing_local', 'past_due'].includes(s) ? s : null;
}
async function _showPlanBannerIfNeeded({ refresh = false } = {}) {
  try {
    // On app open with signal: a fresh token, so an expired trial shows now and
    // not at the next hourly refresh. A network failure keeps the old session.
    // A token minted in the last 5 minutes already carries the current plan.
    const iat = _storedToken().iat;
    if (refresh && navigator.onLine && !(iat && Date.now() / 1000 - iat < 300)) {
      await Promise.race([sbClient.auth.refreshSession().catch(() => null),
        new Promise(r => setTimeout(r, 5000))]);
    }
    const el = document.getElementById('dashPlanBanner');
    if (!el) return;
    el.hidden = !orgPlanReadOnly();
    const t = (k) => window.Ordinex?.i18n?.t(k) ?? k;
    if (!el.hidden) el.textContent = _storedPlanClaims().org_role === 'admin' ? t('plan.readOnlyAdmin') : t('plan.readOnlyWorker');
  } catch { /* banner is best-effort */ }
}

// ── Project loading ───────────────────────────────────────────────────────────
async function loadUserProjects() {
  try {
    let rows;
    if (currentProfile.role === 'worker') {
      // A worker's jobs come from the time clock (clock_context(), spec 6.1): the jobs of his
      // foremen, or every active job when he has none. None while the module is off (he boots
      // on Today, as before the clock) and none when no org is known: fails closed. The org is
      // resolved here because currentOrg is usually not set yet at this point of the login.
      const ctx = typeof _loadClockContext === 'function' ? await _loadClockContext() : null;
      const org = window.currentOrg || await window.Ordinex?.org.load(sbClient).catch(() => null);
      const on = org ? window.Ordinex.timeClock.enabled(org) : false;
      if (on && !ctx) throw new Error('clock context unreadable');   // unknown is not "no jobs": keep the cache
      rows = on ? (ctx?.projects ?? []).map(p => ({ id: p.id, name: p.name, parent_id: p.parent_id ?? null, trade_pack: null })) : [];
    } else if (currentProfile.role === 'admin') {
      // Admins see every active project (mothers included — the admin side
      // needs them; the field pickers in app.js filter them back out).
      // postgrest answers a failed fetch with { error }, not a throw: a failed
      // read must fall to the cache below, never read (and cache) as "no projects".
      const { data, error } = await sbClient
        .from('projects')
        .select('id, name, trade_pack, parent_id')
        .eq('status', 'active')
        .order('name');
      if (error) throw error;
      rows = data ?? [];
    } else {
      // Foremans see only their assigned projects
      const { data, error } = await sbClient
        .from('foreman_projects')
        .select('projects(id, name, status, trade_pack, parent_id)')
        .eq('foreman_id', currentUser.id);
      if (error) throw error;
      rows = data
        ?.map(r => r.projects)
        .filter(p => p && p.status === 'active') ?? [];
    }
    const names = rows.map(p => p.name);
    window.projectTrades = Object.fromEntries(rows.map(p => [p.name, p.trade_pack ?? null]));
    // Sub-projects (buildings): id + parent_id per project, same order/length
    // as `names`. app.js's filterFilableProjects() uses this to hide any
    // project that has buildings under it — crews always file on the
    // building, never the site umbrella. Kept separate from `names` so every
    // existing consumer of window.userProjects (plain name strings) is
    // untouched.
    window.projectRows = rows.map(p => ({ id: p.id, name: p.name, parent_id: p.parent_id ?? null }));
    _cacheProjects(currentUser.id, { names, trades: window.projectTrades, rows: window.projectRows });   // keep a copy for offline boots
    return names;
  } catch (err) {
    console.warn('Could not load projects from Supabase:', err);
    return _loadCachedProjects(currentUser.id) || [];
  }
}

// Mudou pack/projeto no admin? O campo pega na próxima volta ao app —
// sem reinstalar PWA (spec §5.7). Throttle de 60s.
let _lastCtxRevalidate = 0;
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  if (!window.currentUser) return;
  const now = Date.now();
  if (now - _lastCtxRevalidate < 60000) return;
  _lastCtxRevalidate = now;
  window.Ordinex?.org.load(sbClient).then(org => {
    if (org) {
      window.currentOrg = org;
      if (typeof applyTradePacks === 'function') applyTradePacks();
    }
  }).catch(() => {});
  window.Ordinex?.forms.load(sbClient).catch(() => {});
  loadUserProjects().then(names => {
    window.userProjects = names;
    // Waiting on Today's start card and the office just added a job: open it.
    const home = document.getElementById('homeScreen');
    if (home && home.style.display !== 'none' && document.getElementById('dashStartCard')?.hidden === false
        && typeof _todayNoJobs === 'function' && !_todayNoJobs()) _fieldBootEnter();
  }).catch(() => {});
  // Tasks too (2026-10-19): the catalogue used to load at boot only, so a job
  // that gained tasks while the PWA sat open filed the week as General with
  // no `activity` key at all — invisible to every guard downstream.
  loadProjectTasks(window.currentUser.id).then(t => { window.projectTasks = t; }).catch(() => {});
});

// ── Load documents for a project ─────────────────────────────────────────────
async function loadProjectDocuments(projectName) {
  const { data: project } = await sbClient
    .from('projects').select('id').eq('name', projectName).single();
  if (!project) return [];

  const { data } = await sbClient
    .from('documents')
    .select('*')
    .eq('project_id', project.id)
    .order('created_at', { ascending: false });
  return data ?? [];
}

// ── Logout ────────────────────────────────────────────────────────────────────
async function handleLogout() {
  const uid = currentUser?.id;
  await sbClient.auth.signOut();
  // Clear this user's offline cache (privacy on shared devices)
  if (uid) {
    try {
      localStorage.removeItem('ordinex_profile_' + uid);
      localStorage.removeItem('ordinex_projects_' + uid);
      localStorage.removeItem('ordinex_prod_catalog_' + uid);   // Production Report item list
      localStorage.removeItem('ordinex_prod_daystats_' + uid);  // company per-item day stats (outlier check)
      localStorage.removeItem('ordinexFieldProject:' + uid);    // project-first nav (Task 4)
      localStorage.removeItem('ordinexSubCrews:' + uid);        // sub-crew names (Production Report picker)
      // the timesheet prefill's per-job copies: the crew's punches and GPS of a week
      Object.keys(localStorage).filter(k => k.startsWith('ox_ts_' + uid + '_')).forEach(k => localStorage.removeItem(k));
      // the tailgate's last crew per job (offline prefill): names of the crew
      Object.keys(localStorage).filter(k => k.startsWith('ordinexLastCrew:' + uid + ':')).forEach(k => localStorage.removeItem(k));
    } catch {}
  }
  currentUser    = null;
  currentProfile = null;
  window.currentUser    = null;
  window.currentProfile = null;
  window.userProjects   = null;
  window.fieldProject   = null;   // project-first nav (Task 4)
}

// ── Header updates ────────────────────────────────────────────────────────────
function updateHeaderForUser(profile) {
  // Show logged-in user name in header
  const sub = document.getElementById('headerSubtitle');
  if (sub) sub.textContent = profile.full_name || currentUser.email;

  // Show admin button only for admins
  const adminBtn = document.getElementById('adminBtn');
  if (adminBtn) adminBtn.style.display = profile.role === 'admin' ? 'flex' : 'none';

  // Always show logout + change password buttons for signed-in users
  const logoutBtn = document.getElementById('logoutBtn');
  if (logoutBtn) logoutBtn.style.display = 'flex';
  const changePwdBtn = document.getElementById('changePwdBtn');
  if (changePwdBtn) changePwdBtn.style.display = 'flex';
}

// ── Profile setup (first login — name missing) ────────────────────────────────
function showProfileSetup() {
  document.getElementById('profileSetupScreen').style.display = 'flex';
  document.getElementById('mainApp').style.display            = 'none';
  const nb = document.querySelector('.nav-bar');
  const pw = document.querySelector('.progress-wrap');
  if (nb) nb.style.display = 'none';
  if (pw) pw.style.display = 'none';
  setTimeout(() => document.getElementById('profileNameInput')?.focus(), 100);
}

async function saveProfileName() {
  const input = document.getElementById('profileNameInput');
  const errEl = document.getElementById('profileSetupError');
  const btn   = document.getElementById('profileSetupBtn');
  const name  = (input?.value || '').trim();

  if (!name) { if (errEl) errEl.textContent = 'Please enter your name.'; return; }
  if (errEl) errEl.textContent = '';
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

  const { error } = await sbClient
    .from('profiles')
    .update({ full_name: name })
    .eq('id', currentUser.id);

  if (error) {
    if (errEl) errEl.textContent = 'Could not save. Try again.';
    if (btn) { btn.disabled = false; btn.textContent = 'Continue →'; }
    return;
  }

  // Update local profile and proceed to app
  currentProfile.full_name = name;
  _cacheProfile(currentUser.id, currentProfile);
  document.getElementById('profileSetupScreen').style.display = 'none';
  window.userProjects = await loadUserProjects();
  window.projectTasks = await loadProjectTasks(currentUser.id);
  updateHeaderForUser(currentProfile);
  showApp();
  initializeApp();
}

// ── Show app (after successful auth) ─────────────────────────────────────────
function showApp() {
  // '' (not 'block') so the ≥1024px stylesheet grid (sidebar + content) applies
  document.getElementById('mainApp').style.display = '';
  const nb = document.querySelector('.nav-bar');
  const pw = document.querySelector('.progress-wrap');
  if (nb) nb.style.display = '';
  if (pw) pw.style.display = '';
}

// ── Documents drawer (shown per project in the app) ───────────────────────────
async function showDocumentsDrawer(projectName) {
  if (!projectName) return;

  let drawer = document.getElementById('docsDrawer');
  if (!drawer) {
    drawer = document.createElement('div');
    drawer.id = 'docsDrawer';
    drawer.className = 'docs-drawer';
    document.body.appendChild(drawer);
  }

  drawer.innerHTML = `
    <div class="docs-drawer-inner">
      <div class="docs-drawer-header">
        <span>📄 Documents</span>
        <button class="docs-drawer-close" onclick="document.getElementById('docsDrawer').classList.remove('open')">✕</button>
      </div>
      <div class="docs-drawer-project">${_authEsc(projectName)}</div>
      <div class="docs-drawer-list" id="docsDrawerList">
        <div class="docs-loading">Loading…</div>
      </div>
    </div>`;

  drawer.classList.add('open');

  const docs = await loadProjectDocuments(projectName);
  const list = document.getElementById('docsDrawerList');
  if (!list) return;

  if (!docs.length) {
    list.innerHTML = '<div class="docs-empty">No documents uploaded for this project yet.</div>';
    return;
  }

  const typeLabel = {
    change_order: 'Change Order', drawing: 'Drawing',
    rfi: 'RFI', submittal: 'Submittal', specification: 'Specification', general: 'Document'
  };
  const typeIcon = {
    change_order: '📋', drawing: '📐',
    rfi: '🔄', submittal: '📩', specification: '📑', general: '📄'
  };

  list.innerHTML = docs.map(d => {
    const safeUrl = _authSafeUrl(d.file_url);
    // No safe scheme (or empty file_url) → render as inert text instead of a
    // dead/dangerous link, rather than silently dropping the row.
    const openTag = safeUrl
      ? `<a class="doc-item" href="${safeUrl}" target="_blank" rel="noopener">`
      : `<div class="doc-item doc-item--nolink" title="Link unavailable">`;
    const closeTag = safeUrl ? '</a>' : '</div>';
    return `
    ${openTag}
      <span class="doc-icon">${typeIcon[d.type] || '📄'}</span>
      <span class="doc-info">
        <span class="doc-title">${_authEsc(d.title)}</span>
        <span class="doc-meta">${typeLabel[d.type] || 'Document'} · ${new Date(d.created_at).toLocaleDateString()}</span>
      </span>
      <span class="doc-open">↗</span>
    ${closeTag}`;
  }).join('');
}
